import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import * as Excel from 'exceljs/dist/exceljs.js';
import { angularMath } from 'angular-ts-math';

import { Question } from './question';
import { ConsoleService } from './console.service';

@Injectable()
export class QuestionService {
  decks: Map<string, Question[]> = new Map();


  constructor(
    private http: HttpClient,
    private consoleService: ConsoleService
  ) { }


  /* Helper function that will randomly shuffle an array of questions. */
  shuffle(array: Question[]): Question[] {
    let currentIndex = array.length;
    let temporaryValue;
    let randomIndex;

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = angularMath.getIntegerRandomRange(0, currentIndex);
      currentIndex -= 1;

      // And swap it with the current element.
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }

    return array;
  }


  /* Called once on init to load the questions from the Excel file on the server into memory in the browser. */
  loadDeck() {
    return new Promise((resolve, reject) => {
      let url = 'questions.xlsx';
      let workbook = new Excel.Workbook();
      this.http.get(url, {responseType: 'arraybuffer'}).subscribe((response) => {
        workbook.xlsx.load(response).then(() => {
          console.log('Loaded workbook', workbook);
          // Load each sheet in the workbook into the decks using the sheet name as the map key.
          workbook.eachSheet((worksheet, sheetId) => {
            const name = worksheet.name;
            this.decks[name] = [];
            this.consoleService.log('Loading sheet from questions.xlsx: "' + name + '"');

            // Iterate through each row in the workbook and parse questions.
            worksheet.eachRow((row, rowNumber) => {
              // Skip the first row, it contains only header information.
              if (rowNumber > 1) {
                // Parse a question and append it to the deck.
                let question = new Question;

                question.question = row.getCell(1).value;
                question.red = row.getCell(2).value;
                question.yellow = row.getCell(3).value;
                question.blue = row.getCell(4).value;
                question.answer = row.getCell(5).value.toLowerCase();
                question.details = row.getCell(6).value;

                this.decks[name].push(question);
              }
            });

            this.consoleService.log('"' + name + '" contained ' + this.decks[name].length + ' questions.');
          });

          resolve(this.decks);
        });
      });
    });
  }


  /* This function is used to return a shuffled deck for game play.  The entire deck is shuffled and returned which
    * should be more questions than needed to play the full game but ensures that questions are not repeated for
    * players in the same round. */
  getDeck(sheet: string, limit=null): Question[] {
    // Shuffling the full deck and returning a slice ensures that no question will be duplicated within a game.
    let clone = this.decks[sheet].slice(0);
    let shuffled = this.shuffle(clone);
    return (limit) ? shuffled.slice(0, limit) : shuffled;
  }
}
